/**
 * Shared helper utilities used by the collection builder to translate abstract
 * collection definitions into REST-friendly identifiers, URLs, and payloads.
 */
import type { CollectionBuildRequestConfig } from './CollectionBuilder.types'

const PLACEHOLDER_PATTERN = /\[([^\]]+)\]/g

export class PlaceholderResolutionError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PlaceholderResolutionError'
  }
}

/**
 * Normalises arbitrary strings so they can be safely used as DOM or query keys.
 */
export const sanitizeForId = (input: string) =>
  input
    .replace(/[^\w-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .replace(/^-+|-+$/g, '')

/**
 * Indicates whether the provided endpoint includes placeholder markers that need
 * to be resolved before the request is sent.
 */
export function hasEndpointPlaceholders(endpoint: string): boolean {
  PLACEHOLDER_PATTERN.lastIndex = 0
  const result = PLACEHOLDER_PATTERN.test(endpoint)
  PLACEHOLDER_PATTERN.lastIndex = 0
  return result
}

/**
 * Substitutes placeholder segments such as `[workspaceId]` with values from the
 * provided record. Throws when a placeholder cannot be satisfied to avoid
 * silently dispatching malformed requests.
 */
export function resolveEndpointPlaceholders(template: string, data: Record<string, unknown>): string {
  PLACEHOLDER_PATTERN.lastIndex = 0
  return template.replace(PLACEHOLDER_PATTERN, (_match, key: string) => {
    const value = data?.[key]
    if (value === undefined || value === null)
      throw new PlaceholderResolutionError(`Missing value for endpoint placeholder "${key}"`)
    return encodeURIComponent(String(value))
  })
}

/**
 * Resolves the effective API endpoint, expanding placeholder segments using the
 * provided record (or collection filters) and appending resource identifiers
 * when supplied.
 */
export function resolveApiEndpoint(
  config: CollectionBuildRequestConfig,
  options: {
    record?: Record<string, unknown>
    resourceId?: string | number
  } = {},
): string {
  const { apiEndpoint, table } = config
  if (!apiEndpoint) throw new Error(`apiEndpoint must be configured for table ${table}`)

  const placeholderSource = options.record ?? config.where

  let resolvedEndpoint = apiEndpoint
  if (hasEndpointPlaceholders(apiEndpoint)) {
    if (!placeholderSource)
      throw new PlaceholderResolutionError(`Missing context to resolve endpoint placeholders for table ${table}`)
    resolvedEndpoint = resolveEndpointPlaceholders(apiEndpoint, placeholderSource)
  }

  if (options.resourceId === undefined || options.resourceId === null) return resolvedEndpoint

  return buildResourceUrl(resolvedEndpoint, options.resourceId)
}

function toSnakeCase(value: string) {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[-\s]+/g, '_')
    .toLowerCase()
}

const quoteIdentifier = (value: string) => `"${value.replace(/"/g, '""')}"`

const quoteString = (value: string) => `'${value.replace(/'/g, "''")}'`

/**
 * Extracts and validates the configured partition key value so downstream
 * operations can reference the correct dataset slice.
 */
export function ensurePartitionKey(config: CollectionBuildRequestConfig): string | undefined {
  const { partitionKey, where, table, key } = config
  const isPartitionless = key === '*' || !partitionKey
  if (isPartitionless) return undefined

  if (!where) throw new Error(`Partition key and where clause must be provided for table ${table}`)

  const partitionValue = where?.[partitionKey as keyof typeof where]
  if (partitionValue === undefined || partitionValue === null)
    throw new Error(`Partition key "${partitionKey}" must be provided in the where clause for table ${table}`)
  return String(partitionValue)
}

/**
 * Generates a stable identifier for a collection using the table name and the
 * active `where` filters so cache entries remain distinct per constraint set.
 */
export function buildCollectionId(config: CollectionBuildRequestConfig): string {
  const _partitionSegment = ensurePartitionKey(config)
  const segments: string[] = []
  const table = config.table.replace(/"/g, '')

  for (const [field, value] of Object.entries(config.where ?? {})) {
    const fieldSegment = toSnakeCase(field)
    const sanitizedValue = sanitizeForId(String(value))
    const segmentValue = `${fieldSegment}:${sanitizedValue}`
    segments.push(segmentValue)
  }

  const suffix = segments.filter(Boolean).join('::')
  return suffix ? `${table}::${suffix}` : table
}

/**
 * Produces a SQL-safe `WHERE` clause fragment from the provided filter map.
 */
export function buildWhereClause(where?: Record<string, unknown>) {
  if (!where) return undefined

  const parts: string[] = []

  for (const [rawKey, rawValue] of Object.entries(where)) {
    if (rawValue === undefined || rawValue === null) continue
    const column = quoteIdentifier(toSnakeCase(rawKey))

    if (Array.isArray(rawValue)) {
      if (rawValue.length === 0) {
        parts.push('1=0')
        continue
      }
      const values = rawValue
        .filter((value) => value !== undefined && value !== null)
        .map((value) => quoteString(String(value)))
      if (values.length === 0) {
        parts.push('1=0')
        continue
      }
      parts.push(`${column} IN (${values.join(',')})`)
      continue
    }

    switch (typeof rawValue) {
      case 'string':
      case 'number':
      case 'bigint':
      case 'boolean':
        parts.push(`${column} = ${quoteString(String(rawValue))}`)
        break
      default:
        break
    }
  }

  if (parts.length === 0) return undefined
  return parts.join(' AND ')
}

/**
 * Wraps a transaction id (or collection of ids) in the shape expected by
 * reactive-db mutation handlers, defaulting to a generated timestamp when the
 * backend omits one.
 */
export function createMutationResult(txid?: number | number[]) {
  return { txid: txid ?? Date.now() }
}

/**
 * Attempts to read a transaction identifier from heterogeneous payload shapes
 * that may be returned by API endpoints.
 */
export function extractTxid(payload: unknown): number | undefined {
  if (!payload || typeof payload !== 'object') return undefined

  const candidate = (payload as Record<string, unknown>).txid
  if (candidate === undefined || candidate === null) return undefined

  if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate

  if (typeof candidate === 'string') {
    const parsed = Number.parseFloat(candidate)
    if (!Number.isNaN(parsed)) return parsed
  }

  if (Array.isArray(candidate)) {
    const last = candidate.at(-1)
    if (typeof last === 'number' && Number.isFinite(last)) return last
  }

  return undefined
}

/**
 * Creates a resource-specific endpoint by appending a URL-encoded key to the
 * provided base path.
 */
export function buildResourceUrl(base: string, key: string | number): string {
  const trimmed = base.endsWith('/') ? base.slice(0, -1) : base
  const encodedKey = encodeURIComponent(String(key))
  return `${trimmed}/${encodedKey}`
}

/**
 * Removes `undefined` values from change sets so we do not send sparse
 * properties that could wipe data on the backend.
 */
export function normaliseChanges(entry: Record<string, unknown>) {
  return Object.fromEntries(Object.entries(entry).filter(([, value]) => value !== undefined))
}

/**
 * Constructs a query string from collection filters, supporting multi-value
 * arrays that generate repeated query parameters.
 */
export function buildSearchParams(config: CollectionBuildRequestConfig): string {
  const params = new URLSearchParams()
  for (const [rawKey, rawValue] of Object.entries(config.where ?? {})) {
    if (rawValue === undefined || rawValue === null) continue

    if (Array.isArray(rawValue)) {
      for (const value of rawValue) {
        if (value === undefined || value === null) continue
        params.append(rawKey, String(value))
      }
      continue
    }
    params.append(rawKey, String(rawValue))
  }

  return params.toString()
}
